import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import KybStatusCard from "../../components/KybStatus/KybStatusCard";
import AddEntityModal from "../../components/Modal/AddEntityModal";
import AddShareholderModal from "../../components/Modal/AddShareholderModal";
import ConfirmDeleteModal from "../../components/Modal/ConfirmDeleteModal";
import {
  type EntityRegistrationPayload,
  getMediaDisplayUrl,
  type ShareholderPayload,
  uploadCompanyDocument,
} from "../../config/api";
import { uploadAccept, uploadHint, validateUploadFile } from "../../config/uploads";
import { EMPTY_VALUE, formatDate } from "../../i18n/format";
import {
  formatScore,
  hasTrustBadge,
  isAssessed,
  scoreBarWidth,
  trustBadgeDescription,
  trustBadgeLabel,
} from "../../lib/trustLabels";
import {
  useCompaniesMyCompaniesRetrieve,
  useVerificationMyStatusRetrieve,
} from "../../services/companies/generated/companies/companies";
import {
  useCompaniesDocumentsDestroy,
  useCompaniesDocumentsList,
} from "../../services/companies/generated/companies-documents/companies-documents";
import {
  useCompaniesEntityRegistrationsCreate,
  useCompaniesEntityRegistrationsDestroy,
  useCompaniesEntityRegistrationsList,
  useCompaniesEntityRegistrationsUpdate,
} from "../../services/companies/generated/companies-entity-registration/companies-entity-registration";
import {
  useCompaniesShareholdersCreate,
  useCompaniesShareholdersDestroy,
  useCompaniesShareholdersList,
  useCompaniesShareholdersUpdate,
} from "../../services/companies/generated/companies-shareholders/companies-shareholders";
import type {
  CompanyDocument,
  EntityRegistration,
  ShareholderIdentification as Shareholder,
} from "../../services/companies/generated/model";
import "./TrustCompliancePage.css";
import { getApiErrorMessage } from "../../lib/apiError";

/** Shared helper; keeps the "Your company must be approved…" message for 403s. */
const mutationErrorMessage = getApiErrorMessage;

const TrustCompliancePage = () => {
  const { t } = useTranslation(["company", "common"]);
  const queryClient = useQueryClient();

  // Compliance status, scores and the trust badge all live on the Company
  // record (set by moderators), so every card on this page reads from it —
  // nothing is recomputed or hand-typed client-side.
  const { data: myCompaniesData, isLoading: companyLoading } = useCompaniesMyCompaniesRetrieve();
  const myCompany = myCompaniesData?.results?.[0];
  const badgeReviewed = hasTrustBadge(myCompany);
  // KYB: high-level status and the public badge only — never providers, hits or scores.
  const { data: verification } = useVerificationMyStatusRetrieve();
  const overallScore = myCompany?.overallComplianceScore;

  // Edits to registrations / shareholders / documents send records back to
  // moderator review, which can pause the badge — refresh the company record.
  const invalidateCompany = () => queryClient.invalidateQueries({ queryKey: ["/api/v1/companies/myCompanies/"] });

  const {
    data: entitiesData,
    isLoading: entitiesLoading,
    error: entitiesQueryError,
  } = useCompaniesEntityRegistrationsList();

  const {
    data: shareholdersData,
    isLoading: shareholdersLoading,
    error: shareholdersQueryError,
  } = useCompaniesShareholdersList();

  // Document `fileUrl`s are short-lived signed links (private storage), so
  // refetch eagerly instead of relying on the 5-minute global staleTime.
  const {
    data: documentsData,
    isLoading: documentsLoading,
    error: documentsQueryError,
  } = useCompaniesDocumentsList(undefined, {
    query: { staleTime: 60 * 1000, refetchOnMount: "always", refetchOnWindowFocus: true },
  });

  // Extract data from paginated responses
  const entities = entitiesData?.results || [];
  const shareholders = shareholdersData?.results || [];
  const documents = documentsData?.results || [];

  // Format errors for display
  const entitiesError = entitiesQueryError ? t("trust.entities.loadFailed") : null;
  const shareholdersError = shareholdersQueryError ? t("trust.shareholders.loadFailed") : null;
  const documentsError = documentsQueryError ? t("trust.documents.loadFailed") : null;

  // Mutation hooks
  const createEntityMutation = useCompaniesEntityRegistrationsCreate();
  const updateEntityMutation = useCompaniesEntityRegistrationsUpdate();
  const deleteEntityMutation = useCompaniesEntityRegistrationsDestroy();

  const createShareholderMutation = useCompaniesShareholdersCreate();
  const updateShareholderMutation = useCompaniesShareholdersUpdate();
  const deleteShareholderMutation = useCompaniesShareholdersDestroy();

  const createDocumentMutation = useMutation({ mutationFn: uploadCompanyDocument });
  const deleteDocumentMutation = useCompaniesDocumentsDestroy();

  // UI state
  const [isEntityModalOpen, setIsEntityModalOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<EntityRegistration | null>(null);
  const [entitySubmitError, setEntitySubmitError] = useState<string | null>(null);
  const [deletingEntity, setDeletingEntity] = useState<EntityRegistration | null>(null);

  const [isShareholderModalOpen, setIsShareholderModalOpen] = useState(false);
  const [editingShareholder, setEditingShareholder] = useState<Shareholder | null>(null);
  const [shareholderSubmitError, setShareholderSubmitError] = useState<string | null>(null);
  const [deletingShareholder, setDeletingShareholder] = useState<Shareholder | null>(null);

  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [documentsActionError, setDocumentsActionError] = useState<string | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<CompanyDocument | null>(null);

  const handleOpenAddEntity = () => {
    setEditingEntity(null);
    setEntitySubmitError(null);
    setIsEntityModalOpen(true);
  };

  const handleEditEntity = (entity: EntityRegistration) => {
    setEditingEntity(entity);
    setEntitySubmitError(null);
    setIsEntityModalOpen(true);
  };

  const handleCloseEntityModal = () => {
    setIsEntityModalOpen(false);
    setEditingEntity(null);
    setEntitySubmitError(null);
  };

  const handleSaveEntity = async (data: EntityRegistrationPayload) => {
    setEntitySubmitError(null);

    const mutation = editingEntity?.id != null ? updateEntityMutation : createEntityMutation;
    const payload = editingEntity?.id != null ? { id: editingEntity.id, data } : { data };

    mutation.mutate(payload as any, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["/api/v1/companies/entityRegistrations/"],
        });
        invalidateCompany();
        handleCloseEntityModal();
      },
      onError: (err) => {
        setEntitySubmitError(mutationErrorMessage(err, t("trust.errors.saveFailed")));
      },
    });
  };

  const handleDeleteEntityClick = (entity: EntityRegistration) => {
    setDeletingEntity(entity);
  };

  const handleDeleteEntityConfirm = async () => {
    if (!deletingEntity) return;

    deleteEntityMutation.mutate(
      { id: deletingEntity.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/companies/entityRegistrations/"],
          });
          invalidateCompany();
          setDeletingEntity(null);
        },
        onError: (err) => {
          setEntitySubmitError(mutationErrorMessage(err, t("trust.errors.deleteFailed")));
          setDeletingEntity(null);
        },
      },
    );
  };

  const formatEntityDate = (dateStr?: string | null) =>
    formatDate(dateStr, { year: "numeric", month: "2-digit", day: "2-digit" });

  const getStatusPillClass = (status?: string) => {
    const s = status?.toLowerCase() ?? "";
    if (s === "active") return "trust-compliance__status-pill--success";
    if (s === "pending") return "trust-compliance__status-pill--warning";
    return "trust-compliance__status-pill--muted";
  };

  const handleOpenAddShareholder = () => {
    setEditingShareholder(null);
    setShareholderSubmitError(null);
    setIsShareholderModalOpen(true);
  };

  const handleEditShareholder = (shareholder: Shareholder) => {
    setEditingShareholder(shareholder);
    setShareholderSubmitError(null);
    setIsShareholderModalOpen(true);
  };

  const handleCloseShareholderModal = () => {
    setIsShareholderModalOpen(false);
    setEditingShareholder(null);
    setShareholderSubmitError(null);
  };

  const handleSaveShareholder = async (data: ShareholderPayload) => {
    setShareholderSubmitError(null);

    const mutation = editingShareholder?.id != null ? updateShareholderMutation : createShareholderMutation;
    const payload = editingShareholder?.id != null ? { id: editingShareholder.id, data } : { data };

    mutation.mutate(payload as any, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["/api/v1/companies/shareholders/"],
        });
        invalidateCompany();
        handleCloseShareholderModal();
      },
      onError: (err) => {
        setShareholderSubmitError(mutationErrorMessage(err, t("trust.errors.saveFailed")));
      },
    });
  };

  const handleDeleteShareholderClick = (shareholder: Shareholder) => {
    setDeletingShareholder(shareholder);
  };

  const handleDeleteShareholderConfirm = async () => {
    if (!deletingShareholder) return;

    deleteShareholderMutation.mutate(
      { id: deletingShareholder.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/companies/shareholders/"],
          });
          invalidateCompany();
          setDeletingShareholder(null);
        },
        onError: (err) => {
          setShareholderSubmitError(mutationErrorMessage(err, t("trust.errors.deleteFailed")));
          setDeletingShareholder(null);
        },
      },
    );
  };

  const formatDocDate = (dateStr: string) => formatDate(dateStr, { year: "numeric", month: "2-digit", day: "2-digit" });

  const getFileFormatLabel = (format: string) => {
    if (!format) return EMPTY_VALUE;
    const u = format.toUpperCase();
    if (u === "APPLICATION/PDF" || u === "PDF") return "PDF";
    if (u.startsWith("IMAGE/")) return u.replace("IMAGE/", "");
    return format;
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setDocumentsActionError(null);

    const invalid = validateUploadFile(file, "document");
    if (invalid) {
      setDocumentsActionError(invalid);
      input.value = "";
      return;
    }

    // Upload straight to the documents endpoint: the backend stores the file
    // privately and only ever returns short-lived signed links.
    setDocumentsUploading(true);
    createDocumentMutation.mutate(file, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["/api/v1/companies/documents/"],
        });
        invalidateCompany();
      },
      onError: (err) => {
        setDocumentsActionError(err instanceof Error ? err.message : t("trust.documents.uploadFailed"));
      },
      onSettled: () => {
        setDocumentsUploading(false);
        input.value = "";
      },
    });
  };

  const handleDeleteDocumentClick = (doc: CompanyDocument) => {
    setDeletingDocument(doc);
  };

  const handleDeleteDocumentConfirm = async () => {
    if (!deletingDocument) return;

    deleteDocumentMutation.mutate(
      { id: deletingDocument.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["/api/v1/companies/documents/"],
          });
          invalidateCompany();
          setDocumentsActionError(null);
          setDeletingDocument(null);
        },
        onError: (err) => {
          setDocumentsActionError(mutationErrorMessage(err, t("trust.errors.deleteFailed")));
          setDeletingDocument(null);
        },
      },
    );
  };

  return (
    <DashboardLayout showSearch={false}>
      <div className="trust-compliance">
        <header className="trust-compliance__header">
          <div>
            <h1 className="dashboard__title">{t("trust.title")}</h1>
            <p className="dashboard__subtitle">{t("trust.subtitle")}</p>
          </div>
        </header>

        <section className="trust-compliance__overall-card" aria-label={t("trust.overall.ariaLabel")}>
          <div className="trust-compliance__overall-header">
            <div>
              <h2 className="trust-compliance__overall-title">{t("trust.overall.title")}</h2>
              <p className="trust-compliance__overall-subtitle">
                {companyLoading
                  ? t("trust.loading")
                  : badgeReviewed
                    ? trustBadgeDescription(myCompany) || t("trust.overall.awarded")
                    : t("trust.overall.notReviewed")}
              </p>
            </div>
            <span
              className={`trust-compliance__overall-pill ${badgeReviewed ? "" : "trust-compliance__overall-pill--pending"}`}
            >
              <i className={badgeReviewed ? "fa-regular fa-circle-check" : "fa-regular fa-clock"} />
              {myCompany ? trustBadgeLabel(myCompany) : EMPTY_VALUE}
            </span>
          </div>

          <div className="trust-compliance__overall-progress">
            <div className="trust-compliance__overall-bar">
              <div className="trust-compliance__overall-bar-fill" style={{ width: scoreBarWidth(overallScore) }} />
            </div>
            <div className="trust-compliance__overall-metrics">
              {isAssessed(overallScore) ? (
                <>
                  <span>{t("trust.overall.complete", { value: overallScore })}</span>
                  <span>{t("trust.overall.remaining", { value: 100 - Math.min(100, Math.max(0, overallScore)) })}</span>
                </>
              ) : (
                <span>{t("trust.overall.score", { score: formatScore(overallScore) })}</span>
              )}
            </div>
          </div>
        </section>

        <KybStatusCard status={verification} hasRegistration={entities.length > 0} />

        <section className="trust-compliance__entity-section" aria-label={t("trust.entities.ariaLabel")}>
          <header className="trust-compliance__section-header">
            <div className="trust-compliance__section-title-group">
              <div className="trust-compliance__section-icon trust-compliance__section-icon--blue">
                <i className="fa-regular fa-building" />
              </div>
              <div>
                <h2 className="trust-compliance__section-title">{t("trust.entities.title")}</h2>
              </div>
            </div>
            <button type="button" className="trust-compliance__primary-button" onClick={handleOpenAddEntity}>
              <i className="fa-solid fa-plus" />
              <span>{t("trust.entities.add")}</span>
            </button>
          </header>

          {entitiesError && (
            <div className="trust-compliance__entity-error" role="alert">
              {entitiesError}
              <button
                type="button"
                className="trust-compliance__retry"
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: ["/api/v1/companies/entityRegistrations/"],
                  });
                }}
              >
                Retry
              </button>
            </div>
          )}
          {entitiesLoading ? (
            <div className="trust-compliance__entity-loading">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden />
              <span>{t("trust.entities.loading")}</span>
            </div>
          ) : (
            <div className="trust-compliance__entity-grid">
              {entities.length === 0 ? (
                <p className="trust-compliance__entity-empty">{t("trust.entities.empty")}</p>
              ) : (
                entities.map((entity) => (
                  <article key={entity.id} className="trust-compliance__entity-card">
                    <header className="trust-compliance__entity-header">
                      <h3 className="trust-compliance__entity-country">{entity.country || "—"}</h3>
                      <span
                        className={`trust-compliance__entity-status-icon ${
                          entity.status?.toLowerCase() === "active"
                            ? "trust-compliance__entity-status-icon--success"
                            : "trust-compliance__entity-status-icon--warning"
                        }`}
                      >
                        <i
                          className={
                            entity.status?.toLowerCase() === "active"
                              ? "fa-regular fa-circle-check"
                              : "fa-regular fa-clock"
                          }
                        />
                      </span>
                    </header>
                    <dl className="trust-compliance__entity-list">
                      <div className="trust-compliance__entity-row">
                        <dt>{t("trust.entities.registrationNumber")}</dt>
                        <dd>{entity.registrationNumber || "—"}</dd>
                      </div>
                      {entity.subdivision ? (
                        <div className="trust-compliance__entity-row">
                          <dt>{t("trust.entities.subdivision")}</dt>
                          <dd>{entity.subdivision}</dd>
                        </div>
                      ) : null}
                      <div className="trust-compliance__entity-row">
                        <dt>{t("trust.entities.corporationType")}</dt>
                        <dd>{entity.corporationType || "—"}</dd>
                      </div>
                      <div className="trust-compliance__entity-row">
                        <dt>{t("trust.entities.status")}</dt>
                        <dd>
                          <span className={`trust-compliance__status-pill ${getStatusPillClass(entity.status)}`}>
                            {entity.status || "—"}
                          </span>
                        </dd>
                      </div>
                      <div className="trust-compliance__entity-row">
                        <dt>{t("trust.entities.registeredDate")}</dt>
                        <dd>{formatEntityDate(entity.registeredDate)}</dd>
                      </div>
                    </dl>
                    <footer className="trust-compliance__entity-footer">
                      <button
                        type="button"
                        className="trust-compliance__link-button"
                        onClick={() => handleEditEntity(entity)}
                      >
                        <i className="fa-regular fa-pen-to-square" />
                        <span>{t("common:actions.edit")}</span>
                      </button>
                      <button
                        type="button"
                        className="trust-compliance__link-button trust-compliance__link-button--danger"
                        onClick={() => handleDeleteEntityClick(entity)}
                      >
                        <i className="fa-regular fa-trash-can" />
                        <span>{t("common:actions.delete")}</span>
                      </button>
                    </footer>
                  </article>
                ))
              )}
            </div>
          )}
        </section>

        <section className="trust-compliance__shareholder-section" aria-label={t("trust.shareholders.ariaLabel")}>
          <header className="trust-compliance__section-header">
            <div className="trust-compliance__section-title-group">
              <div className="trust-compliance__section-icon trust-compliance__section-icon--purple">
                <i className="fa-regular fa-user" />
              </div>
              <div>
                <h2 className="trust-compliance__section-title">{t("trust.shareholders.title")}</h2>
              </div>
            </div>
            <button type="button" className="trust-compliance__primary-button" onClick={handleOpenAddShareholder}>
              <i className="fa-solid fa-plus" />
              <span>{t("trust.shareholders.add")}</span>
            </button>
          </header>

          {shareholdersError && (
            <div className="trust-compliance__entity-error" role="alert">
              {shareholdersError}
              <button
                type="button"
                className="trust-compliance__retry"
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: ["/api/v1/companies/shareholders/"],
                  });
                }}
              >
                Retry
              </button>
            </div>
          )}
          {shareholdersLoading ? (
            <div className="trust-compliance__entity-loading">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden />
              <span>{t("trust.shareholders.loading")}</span>
            </div>
          ) : (
            <div className="trust-compliance__table trust-compliance__table--shareholders">
              <div className="trust-compliance__table-row trust-compliance__table-row--head">
                <span>{t("common:labels.name")}</span>
                <span>{t("trust.shareholders.ownership")}</span>
                <span>{t("trust.shareholders.nationality")}</span>
                <span>{t("trust.shareholders.idVerified")}</span>
                <span>{t("trust.shareholders.uboStatus")}</span>
                <span className="trust-compliance__table-actions-header">{t("common:labels.actions")}</span>
              </div>

              {shareholders.length === 0 ? (
                <div className="trust-compliance__table-row trust-compliance__table-empty-row">
                  <span>{t("trust.shareholders.empty")}</span>
                </div>
              ) : (
                shareholders.map((sh) => (
                  <div key={sh.id} className="trust-compliance__table-row trust-compliance__table-row--data">
                    <span data-label={t("common:labels.name")} className="trust-compliance__cell-strong">
                      {sh.name || "—"}
                    </span>
                    <span data-label={t("trust.shareholders.ownership")}>
                      {sh.ownershipPercent ? `${sh.ownershipPercent}%` : "—"}
                    </span>
                    <span data-label={t("trust.shareholders.nationality")}>{sh.nationality || "—"}</span>
                    <span data-label={t("trust.shareholders.idVerified")}>
                      {(sh.idVerified ?? "").toLowerCase() === "yes" ? (
                        <span className="trust-compliance__status-pill trust-compliance__status-pill--success">
                          <i className="fa-regular fa-circle-check" aria-hidden /> {t("trust.shareholders.verified")}
                        </span>
                      ) : (
                        <span className="trust-compliance__status-pill trust-compliance__status-pill--muted">
                          {t("trust.shareholders.notVerified")}
                        </span>
                      )}
                    </span>
                    <span data-label={t("trust.shareholders.uboStatus")}>
                      <span
                        className={`trust-compliance__pill trust-compliance__pill--${
                          (sh.uboStatus ?? "").toLowerCase() === "yes" ? "primary" : "muted"
                        }`}
                      >
                        {(sh.uboStatus ?? "no").toLowerCase() === "yes"
                          ? t("common:labels.yes")
                          : t("common:labels.no")}
                      </span>
                    </span>
                    <span className="trust-compliance__table-actions">
                      <button
                        type="button"
                        className="trust-compliance__icon-button"
                        aria-label={t("trust.shareholders.edit")}
                        onClick={() => handleEditShareholder(sh)}
                      >
                        <i className="fa-regular fa-pen-to-square" />
                      </button>
                      <button
                        type="button"
                        className="trust-compliance__icon-button trust-compliance__icon-button--danger"
                        aria-label={t("trust.shareholders.delete")}
                        onClick={() => handleDeleteShareholderClick(sh)}
                      >
                        <i className="fa-regular fa-trash-can" />
                      </button>
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </section>

        <section className="trust-compliance__tiers-section" aria-label={t("trust.tiers.ariaLabel")}>
          <article className="trust-compliance__tiers-card">
            <header className="trust-compliance__section-header trust-compliance__section-header--compact">
              <h2 className="trust-compliance__section-title">{t("trust.tiers.title")}</h2>
              <p className="trust-compliance__section-subtitle">{t("trust.tiers.subtitle")}</p>
            </header>

            <div className="trust-compliance__tier-list">
              <div className="trust-compliance__tier-item">
                <div className="trust-compliance__tier-header">
                  <h3 className="trust-compliance__tier-title">{t("trust.tiers.tier1.title")}</h3>
                  <span className="trust-compliance__tier-percent">
                    {formatScore(myCompany?.tier1BasicVerification)}
                  </span>
                </div>
                <p className="trust-compliance__tier-description">{t("trust.tiers.tier1.description")}</p>
                <div className="trust-compliance__tier-bar">
                  <div
                    className={`trust-compliance__tier-bar-fill ${
                      (myCompany?.tier1BasicVerification ?? 0) >= 100
                        ? "trust-compliance__tier-bar-fill--green"
                        : "trust-compliance__tier-bar-fill--orange"
                    }`}
                    style={{
                      width: scoreBarWidth(myCompany?.tier1BasicVerification),
                    }}
                  />
                </div>
              </div>

              <div className="trust-compliance__tier-item">
                <div className="trust-compliance__tier-header">
                  <h3 className="trust-compliance__tier-title">{t("trust.tiers.tier2.title")}</h3>
                  <span className="trust-compliance__tier-percent">
                    {formatScore(myCompany?.tier2StandardCompliance)}
                  </span>
                </div>
                <p className="trust-compliance__tier-description">{t("trust.tiers.tier2.description")}</p>
                <div className="trust-compliance__tier-bar trust-compliance__tier-bar--soft">
                  <div
                    className={`trust-compliance__tier-bar-fill ${
                      (myCompany?.tier2StandardCompliance ?? 0) >= 100
                        ? "trust-compliance__tier-bar-fill--green"
                        : "trust-compliance__tier-bar-fill--orange"
                    }`}
                    style={{
                      width: scoreBarWidth(myCompany?.tier2StandardCompliance),
                    }}
                  />
                </div>
              </div>

              <div className="trust-compliance__tier-item">
                <div className="trust-compliance__tier-header">
                  <h3 className="trust-compliance__tier-title">{t("trust.tiers.tier3.title")}</h3>
                  <span className="trust-compliance__tier-percent">
                    {formatScore(myCompany?.tier3AdvancedDueDiligence)}
                  </span>
                </div>
                <p className="trust-compliance__tier-description">{t("trust.tiers.tier3.description")}</p>
                <div className="trust-compliance__tier-bar trust-compliance__tier-bar--soft">
                  <div
                    className={`trust-compliance__tier-bar-fill ${
                      (myCompany?.tier3AdvancedDueDiligence ?? 0) >= 100
                        ? "trust-compliance__tier-bar-fill--green"
                        : "trust-compliance__tier-bar-fill--orange"
                    }`}
                    style={{
                      width: scoreBarWidth(myCompany?.tier3AdvancedDueDiligence),
                    }}
                  />
                </div>
              </div>
            </div>
          </article>
        </section>

        <section className="trust-compliance__documents-section" aria-label={t("trust.documents.ariaLabel")}>
          <header className="trust-compliance__section-header">
            <div className="trust-compliance__section-title-group">
              <div className="trust-compliance__section-icon trust-compliance__section-icon--orange">
                <i className="fa-regular fa-file-lines" />
              </div>
              <div>
                <h2 className="trust-compliance__section-title">{t("trust.documents.title")}</h2>
                <p className="trust-compliance__section-subtitle">{t("trust.documents.subtitle")}</p>
              </div>
            </div>
          </header>

          {documentsActionError && (
            <div className="trust-compliance__entity-error" role="alert">
              {documentsActionError}
            </div>
          )}

          {documentsError && (
            <div className="trust-compliance__entity-error" role="alert">
              {documentsError}
              <button
                type="button"
                className="trust-compliance__retry"
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: ["/api/v1/companies/documents/"],
                  });
                }}
              >
                Retry
              </button>
            </div>
          )}
          <div className="trust-compliance__upload-card">
            <label className="trust-compliance__upload-area" aria-busy={documentsUploading}>
              <input
                type="file"
                className="trust-compliance__upload-input"
                accept={uploadAccept("document")}
                onChange={handleDocumentUpload}
                disabled={documentsUploading}
              />
              {documentsUploading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin trust-compliance__upload-icon" aria-hidden />
                  <p className="trust-compliance__upload-title">{t("trust.documents.uploading")}</p>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-arrow-up-from-bracket trust-compliance__upload-icon" />
                  <p className="trust-compliance__upload-title">{t("trust.documents.clickToUpload")}</p>
                  <p className="trust-compliance__upload-subtitle">{uploadHint("document")}</p>
                </>
              )}
            </label>
          </div>

          {documentsLoading ? (
            <div className="trust-compliance__entity-loading">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden />
              <span>{t("trust.documents.loading")}</span>
            </div>
          ) : (
            <div className="trust-compliance__table trust-compliance__table--documents">
              <div className="trust-compliance__table-row trust-compliance__table-row--head">
                <span>{t("trust.documents.name")}</span>
                <span>{t("trust.documents.type")}</span>
                <span>{t("trust.documents.uploaded")}</span>
                <span>{t("common:labels.status")}</span>
                <span className="trust-compliance__table-actions-header">{t("common:labels.actions")}</span>
              </div>

              {documents.length === 0 ? (
                <div className="trust-compliance__table-row trust-compliance__table-empty-row">
                  <span>{t("trust.documents.empty")}</span>
                </div>
              ) : (
                documents.map((doc) => {
                  const statusKey = (doc.status ?? "").toLowerCase();
                  const statusVariant =
                    statusKey === "approved" || statusKey === "verified"
                      ? "success"
                      : statusKey === "rejected"
                        ? "danger"
                        : "warning";
                  const statusLabel = doc.status ? doc.status.charAt(0).toUpperCase() + doc.status.slice(1) : "—";
                  return (
                    <div key={doc.id} className="trust-compliance__table-row trust-compliance__table-row--data">
                      <span data-label={t("trust.documents.name")} className="trust-compliance__document-name">
                        <i className="fa-regular fa-file" aria-hidden />
                        <span className="trust-compliance__cell-strong">{doc.fileName || "—"}</span>
                      </span>
                      <span data-label={t("trust.documents.type")}>{getFileFormatLabel(doc.fileFormat) || "—"}</span>
                      <span data-label={t("trust.documents.uploaded")}>{formatDocDate(doc.createdAt)}</span>
                      <span data-label={t("common:labels.status")}>
                        <span
                          className={`trust-compliance__status-pill trust-compliance__status-pill--${statusVariant}`}
                        >
                          {statusLabel}
                        </span>
                      </span>
                      <span className="trust-compliance__table-actions">
                        {doc.fileUrl && (
                          <a
                            href={getMediaDisplayUrl(doc.fileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="trust-compliance__icon-button"
                            aria-label={t("trust.documents.view")}
                          >
                            <i className="fa-regular fa-eye" />
                          </a>
                        )}
                        {doc.fileUrl && (
                          <a
                            href={getMediaDisplayUrl(doc.fileUrl)}
                            download={doc.fileName}
                            className="trust-compliance__icon-button"
                            aria-label={t("trust.documents.download")}
                          >
                            <i className="fa-solid fa-download" />
                          </a>
                        )}
                        <button
                          type="button"
                          className="trust-compliance__icon-button trust-compliance__icon-button--danger"
                          aria-label={t("trust.documents.delete")}
                          onClick={() => handleDeleteDocumentClick(doc)}
                        >
                          <i className="fa-regular fa-trash-can" />
                        </button>
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </section>

        <AddEntityModal
          isOpen={isEntityModalOpen}
          onClose={handleCloseEntityModal}
          editEntity={editingEntity as any}
          onSave={handleSaveEntity}
          isSubmitting={createEntityMutation.isPending || updateEntityMutation.isPending}
          errorMessage={entitySubmitError}
        />

        <ConfirmDeleteModal
          isOpen={Boolean(deletingEntity)}
          onClose={() => setDeletingEntity(null)}
          onConfirm={handleDeleteEntityConfirm}
          itemName={
            deletingEntity
              ? `${deletingEntity.country} (${deletingEntity.registrationNumber})`
              : t("trust.entities.deleteFallback")
          }
        />

        <AddShareholderModal
          isOpen={isShareholderModalOpen}
          onClose={handleCloseShareholderModal}
          editShareholder={editingShareholder as any}
          onSave={handleSaveShareholder}
          isSubmitting={createShareholderMutation.isPending || updateShareholderMutation.isPending}
          errorMessage={shareholderSubmitError}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={Boolean(deletingShareholder)}
        onClose={() => setDeletingShareholder(null)}
        onConfirm={handleDeleteShareholderConfirm}
        itemName={deletingShareholder ? deletingShareholder.name : t("trust.shareholders.deleteFallback")}
      />

      <ConfirmDeleteModal
        isOpen={Boolean(deletingDocument)}
        onClose={() => setDeletingDocument(null)}
        onConfirm={handleDeleteDocumentConfirm}
        itemName={deletingDocument ? deletingDocument.fileName : t("trust.documents.deleteFallback")}
      />
    </DashboardLayout>
  );
};

export default TrustCompliancePage;
